import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AdminAccessGroupFields } from "@/components/pages/users/components/AdminAccessGroupFields";
import { GroupCreateUsersTable } from "@/components/pages/users/components/GroupCreateUsersTable";
import { useTenants } from "@/components/pages/tenants/hooks/useTenants";
import {
  useAccessGroup,
  useReplaceAccessGroupMembers,
  useReplaceAccessGroupTenants,
  useUpdateAccessGroup,
} from "@/components/pages/users/hooks/useAccessGroups";
import { useGuesteriaAdmins } from "@/components/pages/users/hooks/useGuesteriaAdmins";
import { buildAdminRows } from "@/components/pages/users/utils/userGroupHelpers";

const USERS_PER_PAGE = 10;
const PICKER_LIMIT = 200;

interface AdminAccessGroupEditPageProps {
  id: string;
  backTo: string;
  readOnly?: boolean;
}

export const AdminAccessGroupEditPage: React.FC<
  AdminAccessGroupEditPageProps
> = ({ id, backTo, readOnly = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [usersSearch, setUsersSearch] = useState("");
  const [debouncedUsersSearch, setDebouncedUsersSearch] = useState("");
  const [tenantSearch, setTenantSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialized, setInitialized] = useState(false);

  const { data: group, isLoading: isGroupLoading } = useAccessGroup(
    "admin",
    id,
  );
  const updateMutation = useUpdateAccessGroup("admin", id);
  const replaceMembers = useReplaceAccessGroupMembers("admin", id);
  const replaceTenants = useReplaceAccessGroupTenants(id);

  useEffect(() => {
    if (group && !initialized) {
      setGroupName(group.name);
      setDescription(group.description ?? "");
      if ("members" in group) {
        setSelectedUserIds(group.members.map((m) => m.userId));
      }
      if ("tenants" in group) {
        setSelectedTenantIds(group.tenants.map((t) => t.tenantId));
      }
      setInitialized(true);
    }
  }, [group, initialized]);

  const onBack = useCallback(() => {
    navigate(backTo);
  }, [backTo, navigate]);

  const adminUserFilters = useMemo(
    () => ({
      enabled: "true" as const,
      limit: USERS_PER_PAGE,
      offset: (currentPage - 1) * USERS_PER_PAGE,
      search: debouncedUsersSearch || undefined,
      sortBy: "email" as const,
      sortOrder: "asc" as const,
    }),
    [currentPage, debouncedUsersSearch],
  );

  const tenantPickerQuery = useMemo(
    () => ({
      limit: PICKER_LIMIT,
      offset: 0,
      sortBy: "name" as const,
      sortOrder: "asc" as const,
    }),
    [],
  );

  const { data: guesteriaAdminsResponse, isLoading: isAdminsLoading } =
    useGuesteriaAdmins(adminUserFilters);
  const { data: tenantsResponse, isLoading: isTenantsLoading } = useTenants(
    tenantPickerQuery,
    true,
  );

  const guesteriaAdmins = useMemo(
    () => guesteriaAdminsResponse?.items ?? [],
    [guesteriaAdminsResponse?.items],
  );
  const tenants = useMemo(
    () => tenantsResponse?.items ?? [],
    [tenantsResponse?.items],
  );
  const totalPages = Math.max(
    1,
    Math.ceil((guesteriaAdminsResponse?.total ?? 0) / USERS_PER_PAGE),
  );
  const paginatedRows = useMemo(
    () => buildAdminRows(guesteriaAdmins, t),
    [guesteriaAdmins, t],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [usersSearch]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedUsersSearch(usersSearch.trim());
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [usersSearch]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const areAllVisibleSelected =
    paginatedRows.length > 0 &&
    paginatedRows.every((row) => selectedUserIds.includes(row.id));

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds((current) =>
      current.includes(userId)
        ? current.filter((i) => i !== userId)
        : [...current, userId],
    );
  }, []);

  const toggleAllVisible = useCallback(
    (checked: boolean) => {
      if (checked) {
        const allVisibleIds = paginatedRows.map((row) => row.id);
        setSelectedUserIds((current) =>
          Array.from(new Set([...current, ...allVisibleIds])),
        );
        return;
      }

      const visibleIdsSet = new Set(paginatedRows.map((row) => row.id));
      setSelectedUserIds((current) =>
        current.filter((userId) => !visibleIdsSet.has(userId)),
      );
    },
    [paginatedRows],
  );

  const toggleTenant = useCallback((tenantId: string) => {
    setSelectedTenantIds((current) =>
      current.includes(tenantId)
        ? current.filter((i) => i !== tenantId)
        : [...current, tenantId],
    );
  }, []);

  const onSave = async () => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      toast.error(
        t("users.groups.create.groupNameRequired", "Group name is required"),
      );
      return;
    }

    try {
      await Promise.all([
        updateMutation.mutateAsync({
          name: trimmedName,
          description: description.trim() || null,
        }),
        replaceMembers.mutateAsync(selectedUserIds),
        replaceTenants.mutateAsync(selectedTenantIds),
      ]);
      toast.success(
        t(
          "users.groups.edit.updatedSuccessfully",
          "Access group updated successfully.",
        ),
      );
      navigate(backTo);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t(
              "users.groups.edit.updateFailed",
              "Failed to update access group",
            );
      toast.error(message);
    }
  };

  const isSaving =
    updateMutation.isPending ||
    replaceMembers.isPending ||
    replaceTenants.isPending;

  if (isGroupLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-foreground h-8 rounded-md bg-transparent px-3 py-1 text-base font-normal hover:bg-transparent"
        >
          <Undo2 className="h-6 w-6" />
          {t("users.page.back")}
        </Button>
      </div>

      <div className="bg-background flex flex-col gap-6 rounded-lg p-6">
        <div className="flex flex-col gap-2">
          <Input
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
            placeholder={t("users.groups.create.groupNamePlaceholder", "Name")}
            label={t("users.groups.create.groupNameLabel", "Group Name")}
            maxLength={100}
            disabled={readOnly}
          />
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t(
              "users.groups.create.descriptionPlaceholder",
              "Optional description",
            )}
            maxLength={500}
            className="min-h-[88px]"
            aria-label={t(
              "users.groups.create.descriptionLabel",
              "Description",
            )}
            disabled={readOnly}
          />
        </div>

        <AdminAccessGroupFields
          usersSearch={usersSearch}
          tenantSearch={tenantSearch}
          tenants={tenants}
          selectedTenantIds={selectedTenantIds}
          isTenantsLoading={isTenantsLoading}
          onUsersSearchChange={setUsersSearch}
          onTenantSearchChange={setTenantSearch}
          onToggleTenant={toggleTenant}
          disabled={readOnly}
        />

        <GroupCreateUsersTable
          rows={paginatedRows}
          selectedUserIds={selectedUserIds}
          areAllVisibleSelected={areAllVisibleSelected}
          onToggleUser={toggleUser}
          onToggleAllVisible={toggleAllVisible}
          contextColumnHeader={t(
            "users.groups.create.tenantAccessLabel",
            "Tenant access",
          )}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          isLoading={isAdminsLoading}
          disabled={readOnly}
        />

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onBack} className="h-8 px-4">
            {readOnly ? t("users.page.back") : t("users.form.cancel", "Cancel")}
          </Button>
          {readOnly ? null : (
            <Button
              variant="default"
              className="h-8 px-4"
              onClick={() => void onSave()}
              disabled={isSaving}
            >
              {isSaving
                ? t("users.form.updating", "Updating...")
                : t("users.groups.create.saveChanges", "Save changes")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
